import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { consumeDesignStream } from 'features/sentinelDesign/designStream'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSWRConfig } from 'swr'

import { applyChartPart, clearAgentView, restoreAgentPens } from './agentView'
import {
  type ChatTurn,
  DROPPED_MID_RUN,
  failureMessage,
  foldFrame,
  parseFrame,
  turnText,
} from './chatStream'
import { markAgentWrites, writeKeysForEvent } from './dock/agentWrites'
import { requestDockForAgent } from './dock/dockState'
import { applyLiveEvent, clearModelCard } from './modelCard'
import { agentModelsUrl } from './useAgentModels'
import type { Conversation, useConversations } from './useConversations'

/**
 * One agent conversation. The server owns the transcript — the browser sends a
 * single message and fim-service supplies the history — so a reload, a second
 * tab and a colleague on the next shift all see the same thing.
 *
 * The stream drives three surfaces at once: the transcript here, the model card
 * (values, so a dial has somewhere to travel to) and the write marks (which
 * knob moved, so it lights up). All three, or the panel flashes while showing
 * stale numbers.
 */

export interface ViewerContext {
  /** Tag ids currently plotted on the chart. */
  tags: string[]
  /** The visible time range, ISO. */
  from?: string
  to?: string
  /**
   * The model open on the chart, so "this model" resolves the way "this tag"
   * already did. Picking one from the dropdown drew it but told the agent
   * nothing, so acting on it cost a list_models and a load_model first — a round
   * trip to learn what the user could already see on screen.
   */
  model?: {
    entry_id: string
    name?: string
    kpi?: string
    expr?: string
    gate_expr?: string
    drivers?: string[]
    baseline_window?: string[]
    sample_minutes?: number
    grade_minutes?: number
    last30_sigma?: number
    unsaved?: boolean
  }
}

/** fim-service caps a conversation title at 200 chars; a longer one 422s the
 *  create and the whole send fails. */
const MAX_TITLE_LENGTH = 200

export const useAgentChat = (
  siteId: string | null,
  conversations: ReturnType<typeof useConversations>,
  viewer?: ViewerContext
) => {
  const { t } = useLingui()

  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorization } = useGeckoAuth()
  const { mutate: mutateByKey } = useSWRConfig()
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  /** Bumped whenever the shown conversation changes, so a slower earlier
   *  `open` cannot clobber the one opened after it. */
  const openSeqRef = useRef(0)

  const { create, load, refresh } = conversations

  // A conversation belongs to a site; switching sites starts fresh rather than
  // showing a transcript about a different plant.
  useEffect(() => {
    openSeqRef.current += 1
    setConversationId(null)
    setTurns([])
    setError(null)
    clearAgentView()
  }, [siteId])

  const adopt = useCallback((conversation: Conversation) => {
    setConversationId(conversation.id)
    setTurns(conversation.turns)
    setError(null)
  }, [])

  const open = useCallback(
    async (id: string) => {
      abortRef.current?.abort()
      const seq = ++openSeqRef.current
      setLoading(true)
      setError(null)
      // A different conversation is a different build, so the card starts empty
      // rather than describing a model this transcript never mentions.
      clearModelCard()
      clearAgentView()
      try {
        const conversation = await load(id)
        if (seq !== openSeqRef.current) return
        if (conversation) {
          adopt(conversation)
          // The pens this chat put on the chart come back with it; the window
          // and the band do not.
          restoreAgentPens(conversation.turns)
        }
      } catch {
        if (seq === openSeqRef.current) {
          setError(t`Could not open that conversation.`)
        }
      } finally {
        if (seq === openSeqRef.current) setLoading(false)
      }
    },
    [adopt, load, t]
  )

  const startNew = useCallback(() => {
    abortRef.current?.abort()
    openSeqRef.current += 1
    setConversationId(null)
    setTurns([])
    setError(null)
    setSending(false)
    // A new conversation starts with an empty card, not the last build's knobs.
    clearModelCard()
    clearAgentView()
  }, [])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    setSending(false)
  }, [])

  const send = useCallback(
    async (text: string) => {
      const message = text.trim()
      if (!siteId || !message) return
      abortRef.current?.abort() // send-while-running stops the current run
      setError(null)
      setSending(true)

      const controller = new AbortController()
      abortRef.current = controller
      try {
        const id =
          conversationId ??
          (await create(message.slice(0, MAX_TITLE_LENGTH)))?.id
        if (!id) throw new Error('Could not start a conversation.')
        setConversationId(id)
        setTurns((prev) => [
          ...prev,
          { role: 'user', parts: [{ kind: 'text', text: message }] },
          { role: 'assistant', parts: [] },
        ])

        const response = await fetch(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}` +
            `/sentinel-chat/conversations/${encodeURIComponent(id)}/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'text/event-stream',
              ...(authorization ? { Authorization: authorization } : {}),
            },
            body: JSON.stringify({
              message,
              // What the user is looking at right now, so "this tag" and "this
              // window" mean something to the agent.
              viewer:
                viewer?.tags?.length || viewer?.model ? viewer : undefined,
            }),
            signal: controller.signal,
          }
        )
        if (!response.ok) {
          throw new Error((await response.text()) || `HTTP ${response.status}`)
        }

        const result = await consumeDesignStream(response.body, {
          onProgress: (raw) => {
            const frame = parseFrame(raw)
            if (frame) setTurns((prev) => foldFrame(prev, frame))
            // Revalidate the shared model list on every save, so a re-save of
            // the same entry refreshes it too.
            if (frame?.kind === 'saved' && siteId) {
              void mutateByKey(agentModelsUrl(FIM_SERVICE_URL, siteId))
            }
            // Only live frames drive the chart. Reopening an old conversation
            // shows what the agent did; it does not redo it to the view the
            // user is looking at now.
            if (frame?.kind === 'focus' || frame?.kind === 'pen') {
              applyChartPart(frame)
            }
            applyLiveEvent(raw)
            const writes = writeKeysForEvent(
              raw as Parameters<typeof writeKeysForEvent>[0]
            )
            if (writes.length) {
              markAgentWrites(writes)
              requestDockForAgent()
            }
          },
        })
        if (result === null && !controller.signal.aborted) {
          setError(DROPPED_MID_RUN)
        }
        void refresh()
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(failureMessage(err))
        }
      } finally {
        if (abortRef.current === controller) setSending(false)
      }
    },
    [
      FIM_SERVICE_URL,
      authorization,
      conversationId,
      create,
      mutateByKey,
      refresh,
      siteId,
      viewer,
    ]
  )

  return {
    conversationId,
    turns,
    send,
    stop,
    open,
    startNew,
    sending,
    loading,
    error,
    dismissError: useCallback(() => setError(null), []),
    /** The first thing asked, which is how the drawer names a conversation. */
    title: turns.length ? turnText(turns[0]) : '',
  }
}
